import type { SearchConversationEnriched, SearchQueue } from 'lib-search-types';
import { convertChannelCasing } from 'lib-shared-api-utils';
import type { Channel } from 'lib-shared-types-channels';

import type { WebSocketConversationStatusChangedInboundMessage } from 'middleware/webSocket';

/**
 * Transforms the conversation from the `conversation:status_changed` WebSocket
 * message into a `SearchConversationEnriched` object for optimistic updates.
 */
export function transformWebSocketConversationToSearchConversation(
  wsParams: WebSocketConversationStatusChangedInboundMessage['params'],
): SearchConversationEnriched {
  const { conversation, requester, agent_preference } = wsParams;

  const queue: SearchQueue | undefined = conversation.queue
    ? {
        id: conversation.queue.id,
        name: conversation.queue.name,
        is_restricted: conversation.queue.is_restricted,
        is_default: false,
        is_archived: false,
        available_offer_algorithms: null,
        calculation_method: null,
        offer_abandoned_conversations: false,
        member_list_type: null,
        offer_algorithm: null,
        offer_timeout: null,
        priority: null,
        wrapup_timeout: null,
        queue_thresholds: null,
        is_do_not_offer_enabled: false,
        do_not_offer_timeouts: {},
        personal_agent_offline_timeout: null,
        is_preferred_agent_enabled: false,
        preferred_agent_timeouts: null,
        preferred_agent_offline_timeout: null,
        is_personal_offer_prioritized: false,
        preferred_agent_schedule_id: null,
        personal_agent_schedule_id: null,
        preferred_agent_schedule_id_enabled: false,
        personal_agent_schedule_id_enabled: false,
      }
    : undefined;

  return {
    id: conversation.id,
    status: conversation.status ?? 'open',
    initial_channel: convertChannelCasing(
      conversation.initial_channel,
      'snake',
    ) as Channel,
    initial_direction: conversation.direction ?? 'inbound',
    subject: conversation.attributes?.subject,
    agent_preference: agent_preference ?? undefined,
    assigned_to: conversation.assignment?.agent ?? undefined,
    requested_by: requester,
    queue,
    created_at: conversation.created_at ?? new Date().toISOString(),
    is_abandoned: conversation.is_abandoned ?? undefined,
    is_closed: conversation.is_closed,
    last_message_created_at: conversation.last_message_created_at ?? undefined,
    pending_expiration_at: conversation.pending_expiration_at ?? undefined,
  };
}
